import './index.css';
import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import { forwardRef, useState, useRef, useLayoutEffect } from 'react';
import classnames from 'classnames';
import { useDisabled } from '@hh.ru/magritte-common-use-disabled';
import { Female } from './Placeholder/Female.js';
import { Male } from './Placeholder/Male.js';
import { City } from './Placeholder/City.js';
import { Undefined } from './Placeholder/Undefined.js';
import { A as AvatarSkeleton, s as styles } from './Skeleton-D1OF8yZQ.js';
import { AVATAR_TYPOGRAPHY_MAP, formatLetters } from './utils.js';
import { enrichWithSkeleton } from '@hh.ru/magritte-ui-skeleton';
import { BaseText } from '@hh.ru/magritte-ui-typography/BaseText';

const AvatarForwardRefRenderFunc = ({ mode, size, 'aria-label': ariaLabel, shapeCircle = false, online = false, styleOnline = 'positive', disabled = false, letters, style, icon, image, fallbackMode, placeholder = 'undefined', Element = 'div', containerDataQa = '', ...avatarProps }, ref) => {
    const [isImageLoaded, setLoaded] = useState(false);
    const imgRef = useRef(null);
    const forceDisabled = useDisabled();
    const isComponentDisabled = disabled || forceDisabled;
    const isModeLetters = mode === 'letters' || (mode === 'image' && fallbackMode === 'letters' && !isImageLoaded);
    const isModeIcon = mode === 'icon';
    const isModePlaceholder = mode === 'placeholder' || (mode === 'image' && fallbackMode === 'placeholder' && !isImageLoaded);
    const isModeImage = mode === 'image';
    const additionalAttributes = {};
    if (Element === 'button') {
        additionalAttributes.type = 'button';
    }
    if (isComponentDisabled && Element === 'a') {
        additionalAttributes['aria-disabled'] = true;
        additionalAttributes.tabIndex = -1;
    }
    if (ref) {
        additionalAttributes.ref = ref;
    }
    const classNames = {
        [styles.avatar]: true,
        [styles.avatarShapeCircle]: shapeCircle,
        [styles.avatarModeImage]: mode === 'image' && isImageLoaded,
        [styles.avatarModeLetters]: isModeLetters,
        [styles.avatarModeIcon]: isModeIcon,
        [styles.avatarModePlaceholder]: isModePlaceholder,
        [styles[`avatar_size-${size}`]]: !!size,
        [styles[`avatar_with-online-badge-${size}`]]: online,
    };
    if ((isModeLetters || isModeIcon) && style) {
        classNames[styles[`avatar_style-${style}`]] = !!style;
    }
    useLayoutEffect(() => {
        const handleOnLoad = () => setLoaded(true);
        const handleOnError = () => setLoaded(false);
        const listenOn = imgRef.current;
        if (imgRef.current && image) {
            imgRef.current.addEventListener('load', handleOnLoad);
            imgRef.current.addEventListener('error', handleOnError);
            imgRef.current.src = image;
        }
        return () => {
            if (listenOn) {
                listenOn.removeEventListener('load', handleOnLoad);
                listenOn.removeEventListener('error', handleOnError);
            }
        };
    }, [image]);
    return (jsxs("div", { ...(online || isComponentDisabled
            ? {
                className: classnames({
                    [styles.avatarContainer]: online,
                    [styles.avatarContainerDisabled]: isComponentDisabled,
                    [styles[`avatar-container_size-${size}`]]: online,
                }),
            }
            : {}), ...(containerDataQa ? { 'data-qa': containerDataQa } : {}), children: [jsxs(Element, { ...additionalAttributes, ...avatarProps, className: classnames(classNames), disabled: Element === 'button' ? isComponentDisabled : false, "aria-label": ariaLabel, children: [isModeLetters && (jsx(BaseText, { style: "primary", Element: "span", typography: AVATAR_TYPOGRAPHY_MAP[size], children: formatLetters(letters || '') })), isModeIcon && icon, isModePlaceholder && (jsxs(Fragment, { children: [placeholder === 'female' && jsx(Female, { role: "img", width: size, height: size }), placeholder === 'male' && jsx(Male, { role: "img", width: size, height: size }), placeholder === 'city' && jsx(City, { role: "img", width: size, height: size }), placeholder === 'undefined' && jsx(Undefined, { role: "img", width: size, height: size })] })), isModeImage && (jsx("img", { ref: imgRef, alt: ariaLabel, className: classnames(styles.avatarImage, { [styles.avatarImageHidden]: !isImageLoaded }) }))] }), online && (jsxs(Fragment, { children: [jsx("div", { className: styles[`avatar-focus-border-${size}`] }), jsx("div", { className: classnames(styles[`avatar-online-badge-${size}`], styles[`avatar-online-badge_style_${styleOnline}`]) })] }))] }));
};
const AvatarComponent = forwardRef(AvatarForwardRefRenderFunc);
const Avatar = enrichWithSkeleton(AvatarComponent, AvatarSkeleton);

export { Avatar };
//# sourceMappingURL=Avatar.js.map
